/**
 * Pure view model shared by both web mounts of the pool Rules content: the
 * pool home `Rules` card and the pool settings `contest-rules` slot.
 *
 * This module is pure: no React import, no DOM, no fetch, no generated
 * client import (`plan.md ## 5.29` established the pattern in
 * `joiningCardState.ts`, this module follows it). `.agents/design-prompts/
 * 27-pool-settings.md` section 27.1 is the source of its copy.
 */

export type typePoolRulesContestSection = {
  autoGeneratedText: string;
  commissionerPreamble: string | null;
};

export type typePoolRulesModel = {
  commissionerPreamble: string | null;
  autoGeneratedSummary: string;
  contestSection: typePoolRulesContestSection | null;
};

export type typePoolRulesState =
  | { phase: "loading" }
  | { phase: "failed" }
  | { phase: "ready"; model: typePoolRulesModel };

export const POOL_RULES_LOADING_LABEL = "Loading this pool's rules";
export const POOL_RULES_FAILED_MESSAGE = "We could not load these rules.";
export const POOL_RULES_RETRY_LABEL = "Try again";
export const POOL_RULES_CONTEST_HEADING = "How it works";

function trimToNull(value: string | null): string | null {
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

export function resolvePoolRulesState(input: {
  isLoading: boolean;
  isTransportError: boolean;
  errorCode: string | null;
  commissionerPreamble: string | null;
  autoGeneratedSummary: string | null;
  contest: typePoolRulesContestSection | null;
}): typePoolRulesState {
  if (input.isTransportError || input.errorCode !== null) {
    return { phase: "failed" };
  }

  if (input.isLoading) {
    return { phase: "loading" };
  }

  const trimmedSummary = trimToNull(input.autoGeneratedSummary);
  if (trimmedSummary === null) {
    return { phase: "failed" };
  }

  const contestText =
    input.contest === null ? null : trimToNull(input.contest.autoGeneratedText);
  const contestSection: typePoolRulesContestSection | null =
    contestText === null || input.contest === null
      ? null
      : {
          autoGeneratedText: contestText,
          commissionerPreamble: trimToNull(input.contest.commissionerPreamble),
        };

  return {
    phase: "ready",
    model: {
      commissionerPreamble: trimToNull(input.commissionerPreamble),
      autoGeneratedSummary: trimmedSummary,
      contestSection,
    },
  };
}
